import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Eye, Download, Search, Heart, Copy, X } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TemplatePreset } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { copyToClipboard, getColorValue } from './utils';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { colorPresetService } from '@/lib/colorPresetService';
import { toast } from 'sonner';

// ─── TemplateCard ────────────────────────────────────────────────────────────
// Defined OUTSIDE BrowsePresetsPage so its reference is stable across re-renders.
// If it were defined inside, React would see a new component type on every render
// and unmount+remount all cards (causing the "refresh" flash).
interface TemplateCardProps {
  template: TemplatePreset;
  index: number;
  bookmarkedTemplates: Set<string>;
  onPreview: (template: TemplatePreset) => void;
  onImport: (template: TemplatePreset) => void;
  onBookmark: (id: string) => void;
  onCategoryClick: (cat: string) => void;
}

const TemplateCard = React.memo(({ template, index, bookmarkedTemplates, onPreview, onImport, onBookmark, onCategoryClick }: TemplateCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
  >
    <div
      className="rounded-xl bg-card border border-border/50 shadow-sm hover:shadow-md transition-all cursor-pointer h-full flex flex-col overflow-hidden"
      onClick={() => onPreview(template)}
    >
      {/* Card Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start gap-3">
          {template.logoSvg && (
            <img
              src={template.logoSvg}
              alt="Logo by Brandfetch"
              className="w-9 h-9 flex-shrink-0 object-contain rounded-lg mt-0.5"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-foreground truncate leading-tight">{template.name}</span>
              <Badge
                variant="secondary"
                className="cursor-pointer hover:opacity-80 bg-muted text-muted-foreground border-0 shrink-0 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onCategoryClick(template.category);
                }}
              >
                {template.category}
              </Badge>
            </div>
            {template.description && (
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Color preview */}
      <div className="px-4 pb-3">
        <div className="grid grid-cols-5 h-20 rounded-lg overflow-hidden">
          {template.colors.map((color, idx) => (
            <div
              key={idx}
              className="flex-1 h-full"
              style={{ backgroundColor: color.hex }}
              title={`${color.name}: ${color.hex}`}
            />
          ))}
        </div>
      </div>

      {/* Card Footer — icons LEFT, Import RIGHT */}
      <div className="p-4 pt-3 flex justify-between items-center mt-auto">
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(template);
            }}
            className="w-9 h-9 p-0 flex items-center justify-center rounded-lg bg-muted/80 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Preview"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBookmark(template.id);
            }}
            className={`w-9 h-9 p-0 flex items-center justify-center rounded-lg transition-colors ${
              bookmarkedTemplates.has(template.id)
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                : 'bg-muted/80 text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            title="Bookmark"
          >
            <Heart className={`w-5 h-5 ${bookmarkedTemplates.has(template.id) ? 'fill-current' : ''}`} />
          </button>
        </div>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onImport(template);
          }}
          className="bg-foreground/80 text-background hover:bg-foreground text-xs px-2.5 py-1 h-7 opacity-70 hover:opacity-100 transition-opacity"
        >
          <Download className="w-3 h-3 mr-1" />
          Import
        </Button>
      </div>
    </div>
  </motion.div>
));
TemplateCard.displayName = 'TemplateCard';
// ─────────────────────────────────────────────────────────────────────────────



interface BrowsePresetsPageProps {
  onBack: () => void;
  onImportTemplate: (template: TemplatePreset) => void;
  isGlobalDarkMode?: boolean;
}

export const BrowsePresetsPage: React.FC<BrowsePresetsPageProps> = ({
  onBack,
  onImportTemplate,
}) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = React.useState<TemplatePreset | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [showBookmarked, setShowBookmarked] = React.useState(false);
  const [bookmarkedTemplates, setBookmarkedTemplates] = React.useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = React.useState(12);
  const templatesPerPage = 12;
  const [colorFormat, setColorFormat] = React.useState<'hex' | 'rgb' | 'hsl' | 'oklch'>('oklch');
  const [templatePresets, setTemplatePresets] = React.useState<TemplatePreset[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  React.useEffect(() => {
    const loadTemplatesAndBookmarks = async () => {
      if (!user) return;

      setIsLoadingTemplates(true);
      try {
        const templates = await colorPresetService.getAllTemplates();
        setTemplatePresets(templates);

        if (currentWorkspace) {
          const bookmarks = await colorPresetService.getBookmarkedTemplates(
            currentWorkspace.id,
            user.id
          );
          setBookmarkedTemplates(new Set(bookmarks));
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplatesAndBookmarks();
  }, [currentWorkspace, user]);

  const toggleBookmark = async (templateId: string) => {
    if (!currentWorkspace || !user) {
      toast.error('Please select a workspace first');
      return;
    }

    try {
      const isBookmarked = await colorPresetService.toggleBookmark(
        currentWorkspace.id,
        user.id,
        templateId
      );

      setBookmarkedTemplates(prev => {
        const newBookmarks = new Set(prev);
        if (isBookmarked) {
          newBookmarks.add(templateId);
        } else {
          newBookmarks.delete(templateId);
        }
        return newBookmarks;
      });
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Failed to update bookmark');
    }
  };

  const categories = React.useMemo(() => {
    const uniqueCategories = [...new Set(templatePresets.map(template => template.category))];
    return ['all', ...uniqueCategories.sort()];
  }, [templatePresets]);

  const filteredTemplates = React.useMemo(() => {
    let filtered = templatePresets.filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    if (showBookmarked) {
      filtered = filtered.filter(template => bookmarkedTemplates.has(template.id));
    }

    return filtered;
  }, [templatePresets, searchQuery, selectedCategory, showBookmarked, bookmarkedTemplates]);

  // Reset visible count whenever filters change
  React.useEffect(() => {
    setVisibleCount(12);
  }, [searchQuery, selectedCategory, showBookmarked, bookmarkedTemplates]);

  const hasMore = visibleCount < filteredTemplates.length;
  const currentTemplates = filteredTemplates.slice(0, visibleCount);

  // Infinite scroll sentinel
  React.useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setIsLoadingMore(true);
          setTimeout(() => {
            setVisibleCount(prev => prev + templatesPerPage);
            setIsLoadingMore(false);
          }, 400);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, templatesPerPage]);

  const groupedTemplates = React.useMemo(() => {
    return currentTemplates.reduce((acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category].push(template);
      return acc;
    }, {} as Record<string, TemplatePreset[]>);
  }, [currentTemplates]);


  const handleImport = React.useCallback((template: TemplatePreset) => {
    onImportTemplate(template);
    onBack();
  }, [onImportTemplate, onBack]);

  const handlePreview = React.useCallback((template: TemplatePreset) => {
    setSelectedTemplate(template);
  }, []);

  const handleCategoryClick = React.useCallback((cat: string) => {
    setSelectedCategory(cat);
  }, []);

  // TemplateCard is now defined outside this component — no inline definition needed

  return (
    <div className="min-h-screen bg-background">
      {/* Flat dashboard-style header */}
      <div className="px-4 sm:px-6 md:px-10 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">

          {/* Left: back button + title */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="flex items-center justify-center w-9 h-9 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Back to color presets"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-3xl font-bold text-foreground">Browse Templates</h1>
          </div>

          {/* Right: controls */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* Expandable Search */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search templates..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                    <button
                      onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Category filter */}
            <div className="w-[160px] flex-shrink-0">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger
                  className={`h-10 w-full text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none ${
                    selectedCategory !== 'all'
                      ? 'bg-background shadow-sm text-foreground border-transparent'
                      : 'bg-card border-border text-foreground'
                  }`}
                >
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.slice(1).map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bookmarked filter */}
            <button
              onClick={() => setShowBookmarked(!showBookmarked)}
              className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
                showBookmarked
                  ? 'bg-background shadow-sm text-foreground border-transparent'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground'
              }`}
              aria-label="Bookmarked"
              title="Show bookmarked only"
            >
              <Heart className={`w-4 h-4 ${showBookmarked ? 'fill-current' : ''}`} />
            </button>

          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 md:px-10 pb-8 templates-section">
        {isLoadingTemplates ? (
          <LoadingSpinner size="md" className="py-16" />
        ) : selectedCategory === 'all' ? (
          <div className="grid grid-cols-1 gap-4">
            {currentTemplates.map((template, index) => (
              <TemplateCard
                key={template.id}
                template={template}
                index={index}
                bookmarkedTemplates={bookmarkedTemplates}
                onPreview={handlePreview}
                onImport={handleImport}
                onBookmark={toggleBookmark}
                onCategoryClick={handleCategoryClick}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedTemplates).map(([category, templates]) => (
              <div key={category}>
                <h3 className="text-lg font-semibold mb-4 text-foreground">{category}</h3>
                <div className="grid grid-cols-1 gap-4">
                  {templates.map((template, index) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      index={index}
                      bookmarkedTemplates={bookmarkedTemplates}
                      onPreview={handlePreview}
                      onImport={handleImport}
                      onBookmark={toggleBookmark}
                      onCategoryClick={handleCategoryClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoadingTemplates && filteredTemplates.length === 0 && (
          <div className="text-center py-16">
            <div className="mx-auto w-32 h-32 bg-muted rounded-full flex items-center justify-center mb-6">
              {showBookmarked ? (
                <Heart className="w-12 h-12 text-muted-foreground" />
              ) : (
                <Search className="w-12 h-12 text-muted-foreground" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {showBookmarked ? 'No bookmarked templates' : 'No templates found'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {showBookmarked
                ? 'Start bookmarking templates to see them here'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {!showBookmarked && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="bg-board-column text-foreground border-none hover:bg-muted"
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Infinite scroll sentinel + loader */}
        {!isLoadingTemplates && hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-8">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                Loading more...
              </div>
            )}
          </div>
        )}
        {!isLoadingTemplates && !hasMore && filteredTemplates.length > 0 && (
          <div ref={sentinelRef} />
        )}
      </div>

      {selectedTemplate && (
        <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
          <DialogContent className="bg-popover border border-border text-popover-foreground [&>button]:text-foreground [&>button]:hover:text-foreground [&>button]:hover:bg-muted [&>button]:bg-transparent [&>button]:border-0 [&>button]:p-1 [&>button]:w-6 [&>button]:h-6 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-foreground">
                {selectedTemplate.logoSvg && (
                  <img
                    src={selectedTemplate.logoSvg}
                    alt="Logo by Brandfetch"
                    className="w-8 h-8 flex-shrink-0 object-contain"
                  />
                )}
                {selectedTemplate.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {selectedTemplate.description}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="border border-border rounded-lg overflow-hidden bg-board-column">
                <div className="grid grid-cols-2 bg-muted border-b border-border">
                  <div className="px-4 py-3 text-sm font-medium text-foreground border-r border-border">
                    Colors
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Color Codes</span>
                    <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-8 shrink-0 select-none">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                        {colorFormat}
                      </span>
                      <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            const fmts = ['oklch','hex','rgb','hsl'] as const;
                            const idx = fmts.indexOf(colorFormat);
                            setColorFormat(fmts[idx === 0 ? fmts.length - 1 : idx - 1]);
                          }}
                          className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                          title="Previous format"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const fmts = ['oklch','hex','rgb','hsl'] as const;
                            const idx = fmts.indexOf(colorFormat);
                            setColorFormat(fmts[idx === fmts.length - 1 ? 0 : idx + 1]);
                          }}
                          className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                          title="Next format"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-border">
                  {selectedTemplate.colors.map((color, idx) => (
                    <div key={idx} className="grid grid-cols-2 bg-board-column hover:bg-muted transition-colors">
                      <div className="px-4 py-3 border-r border-border flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded border border-border"
                          style={{ backgroundColor: color.hex }}
                        />
                        <div className="font-medium text-foreground">{color.name}</div>
                      </div>
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="text-sm font-mono font-semibold text-foreground">
                          {getColorValue(color.hex, colorFormat)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(getColorValue(color.hex, colorFormat))}
                          className="p-1 h-6 w-6 bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent border-0 flex-shrink-0"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedTemplate(null)}
                  className="bg-board-column hover:bg-muted text-foreground border border-border"
                >
                  Close
                </Button>
                <Button
                  onClick={() => handleImport(selectedTemplate)}
                  className="bg-foreground text-background hover:bg-foreground/85"
                >
                  Import This Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
